import { useCtx } from '@reatom/npm-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { ArrowUpIcon, Button } from '@blade/ui-kit'

import type { Atom } from '@reatom/framework'

const THRESHOLD = 200
const VIEWPORT_TOP_OFFSET = 6

type Props = {
  scrollAtom: Atom<number>
  onBackToTop: () => void
  /** Pins below page chrome when the table scrolls in the layout scrollport. */
  viewportStickyTop?: number
  floatingActions?: React.ReactNode
}

export const DataTableBackToTop = ({
  onBackToTop,
  scrollAtom,
  viewportStickyTop,
  floatingActions,
}: Props) => {
  const [visible, setVisible] = useState(false)
  const ctx = useCtx()
  const { t } = useTranslation()

  useEffect(() => {
    return ctx.subscribe(scrollAtom, (scroll) => {
      setVisible(scroll > THRESHOLD)
    })
  }, [ctx, scrollAtom])

  if (!visible && !floatingActions) {
    return undefined
  }

  return (
    <FloatingBar $viewportStickyTop={viewportStickyTop}>
      {floatingActions}
      {visible ? (
        <BackToTopButton
          variant="soft"
          size="xxs"
          radius="rounded"
          iconRight={<ArrowUpIcon size={12} />}
          onClick={onBackToTop}
        >
          {t('common.back-to-top')}
        </BackToTopButton>
      ) : undefined}
    </FloatingBar>
  )
}

const FloatingBar = styled.div<{ $viewportStickyTop?: number }>`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
  justify-content: center;
  pointer-events: none;
  z-index: 2;

  ${({ $viewportStickyTop }) =>
    $viewportStickyTop === undefined
      ? css`
          position: absolute;
          left: 0;
          right: 0;
          top: 36px;
        `
      : css`
          position: fixed;
          left: 0;
          right: 0;
          top: ${$viewportStickyTop + VIEWPORT_TOP_OFFSET}px;
        `}
`

const BackToTopButton = styled(Button)`
  --button-radius-rounded: 4px;

  pointer-events: all;
  background: var(--background-2);
  color: var(--text-11);

  gap: 2px;
  padding-block: 5px;
  padding-inline: 10px 6px;
  border-radius: 2px;
  font-size: 11px;

  transition: all 0.2s ease-in-out;
  font-weight: 500;

  & svg {
    margin-top: 1px;
  }

  &:hover {
    color: var(--text-12);
    background: var(--background-3);
  }
`
